/**
 * Admin API Service - General purpose admin API client
 *
 * Provides a simple interface for making admin API calls without
 * needing to create specific services for each endpoint.
 */

import apiClient from "../utils/apiClient";

class AdminApiService {
  constructor() {
    this.baseUrl = "/api/admin";
  }

  /**
   * Make a GET request
   * @param {string} endpoint - API endpoint (e.g., '/bonuses/wheels')
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} API response
   */
  async get(endpoint, params = {}) {
    try {
      const url = `${this.baseUrl}${endpoint}`;

      const response = await apiClient.get(url, { params });

      return response.data;
    } catch (error) {
      // Error handling delegated to _handleError method
      throw this._handleError(error);
    }
  }

  /**
   * Make a POST request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body data
   * @returns {Promise<Object>} API response
   */
  async post(endpoint, data = {}) {
    try {
      const url = `${this.baseUrl}${endpoint}`;

      const response = await apiClient.post(url, data);
      return response.data;
    } catch (error) {
      // Error handling delegated to _handleError method
      throw this._handleError(error);
    }
  }

  /**
   * Make a PATCH request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body data
   * @returns {Promise<Object>} API response
   */
  async patch(endpoint, data = {}) {
    try {
      const url = `${this.baseUrl}${endpoint}`;

      const response = await apiClient.patch(url, data);

      return response.data;
    } catch (error) {
      // Error handling delegated to _handleError method
      throw this._handleError(error);
    }
  }

  /**
   * Make a PUT request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body data
   * @returns {Promise<Object>} API response
   */
  async put(endpoint, data = {}) {
    try {
      const url = `${this.baseUrl}${endpoint}`;

      const response = await apiClient.put(url, data);
      return response.data;
    } catch (error) {
      // Error handling delegated to _handleError method
      throw this._handleError(error);
    }
  }

  /**
   * Make a DELETE request
   * @param {string} endpoint - API endpoint
   * @returns {Promise<Object>} API response
   */
  async delete(endpoint) {
    try {
      const url = `${this.baseUrl}${endpoint}`;

      const response = await apiClient.delete(url);

      return response.data;
    } catch (error) {
      // Error handling delegated to _handleError method
      throw this._handleError(error);
    }
  }

  /**
   * Handle API errors consistently
   * @param {Error} error - The error object
   * @returns {Error} Processed error
   * @private
   */
  _handleError(error) {
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      const message = data?.message || data?.error || `HTTP ${status} Error`;

      const processedError = new Error(message);
      processedError.status = status;
      processedError.data = data;

      return processedError;
    } else if (error.request) {
      // Request was made but no response received
      return new Error("Network error - please check your connection");
    } else {
      // Something else happened
      return new Error(error.message || "An unexpected error occurred");
    }
  }

  // Convenience methods for common bonus endpoints

  /**
   * Get all custom wheels
   * @returns {Promise<Object>} Wheels data
   */
  async getWheels() {
    return this.get("/bonuses/wheels");
  }

  /**
   * Get specific wheel by ID
   * @param {string} wheelId - Wheel ID
   * @returns {Promise<Object>} Wheel data
   */
  async getWheel(wheelId) {
    return this.get(`/bonuses/wheels/${wheelId}`);
  }

  /**
   * Create new wheel
   * @param {Object} wheelData - Wheel configuration
   * @returns {Promise<Object>} Created wheel data
   */
  async createWheel(wheelData) {
    return this.post("/bonuses/wheels", wheelData);
  }

  /**
   * Update existing wheel
   * @param {string} wheelId - Wheel ID
   * @param {Object} wheelData - Updated wheel data
   * @returns {Promise<Object>} Updated wheel data
   */
  async updateWheel(wheelId, wheelData) {
    return this.patch(`/bonuses/wheels/${wheelId}`, wheelData);
  }

  /**
   * Delete wheel
   * @param {string} wheelId - Wheel ID
   * @returns {Promise<Object>} Deletion confirmation
   */
  async deleteWheel(wheelId) {
    return this.delete(`/bonuses/wheels/${wheelId}`);
  }

  // Note: Bonus triggers removed - trigger conditions now configured within bonus plans

  /**
   * Get bonus plans
   * @returns {Promise<Object>} Bonus plans data
   */
  async getBonusPlans() {
    return this.get("/bonuses/plans");
  }

  /**
   * Create bonus plan
   * @param {Object} planData - Bonus plan data
   * @returns {Promise<Object>} Created plan data
   */
  async createBonusPlan(planData) {
    return this.post("/bonuses/plans", planData);
  }

  /**
   * Update bonus plan
   * @param {string} planId - Plan ID
   * @param {Object} planData - Updated plan data
   * @returns {Promise<Object>} Updated plan data
   */
  async updateBonusPlan(planId, planData) {
    return this.patch(`/bonuses/plans/${planId}`, planData);
  }

  /**
   * Delete bonus plan
   * @param {string} planId - Plan ID
   * @returns {Promise<Object>} Success message
   */
  async deleteBonusPlan(planId) {
    return this.delete(`/bonuses/plans/${planId}`);
  }

  /**
   * Get event logs
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Event logs data
   */
  async getEventLogs(params = {}) {
    return this.get("/bonuses/events", params);
  }

  /**
   * Get bonus analytics
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Analytics data
   */
  async getAnalytics(params = {}) {
    return this.get("/bonuses/analytics", params);
  }

  // Email analytics endpoints
  // All accept { from?, to?, window_days? } where from/to are ISO datetimes
  // and window_days is the attribution window (1-90, default 3).

  /**
   * Get email attribution summary (single aggregate row + params echo)
   * @param {Object} params - { from?, to?, window_days? }
   * @returns {Promise<Object>} { summary, params }
   */
  async getEmailAnalyticsSummary(params = {}) {
    return this.get("/email-analytics/summary", params);
  }

  /**
   * Get per-campaign email funnel analytics
   * @param {Object} params - { from?, to?, window_days? }
   * @returns {Promise<Object>} { campaigns, params }
   */
  async getEmailAnalyticsCampaigns(params = {}) {
    return this.get("/email-analytics/campaigns", params);
  }

  /**
   * Get engaged recipients for the drill-down sub-table (paginated)
   * @param {Object} params - { from?, to?, window_days?, campaign?,
   *   campaign_type?, limit?, offset? } (limit default 50, max 200)
   * @returns {Promise<Object>} { recipients, total, params }
   */
  async getEmailAnalyticsRecipients(params = {}) {
    return this.get("/email-analytics/recipients", params);
  }

  /**
   * Get the daily email engagement timeseries for the trend chart
   * @param {Object} params - { from?, to? }
   * @returns {Promise<Object>} { series: [{ day, delivered, unique_opens,
   *   unique_clicks, unsubscribes }] }
   */
  async getEmailAnalyticsTimeseries(params = {}) {
    return this.get("/email-analytics/timeseries", params);
  }

  /**
   * Get acquisition sources report (UTM / referrer attribution).
   * @param {Object} params - { start: ISO string, end: ISO string }
   * @returns {Promise<Object>} { sources: [{ source, signups, depositors,
   *   revenue }] }
   */
  async getAcquisitionSources(params = {}) {
    return this.get("/analytics/acquisition-sources", params);
  }

  /**
   * Get the player activation funnel report.
   * @param {Object} params - { from: ISO string, to: ISO string, source? }
   * @returns {Promise<Object>} { range, stages, dob_gate, by_source }
   */
  async getFunnel(params = {}) {
    return this.get("/analytics/funnel", params);
  }

  // A/B Experiments (Growth Hub). Gated server-side by experiments:read /
  // experiments:write; the UI additionally hides the surface without the read
  // permission. See docs/features/22-AB_EXPERIMENT_FRAMEWORK.md.

  /**
   * List all experiments.
   * @returns {Promise<Object>} { experiments: [...] }
   */
  async getExperiments() {
    return this.get("/experiments");
  }

  /**
   * Get a single experiment by id.
   * @param {string} id - Experiment id
   * @returns {Promise<Object>} experiment object
   */
  async getExperiment(id) {
    return this.get(`/experiments/${id}`);
  }

  /**
   * Get the per-variant funnel + significance report card for an experiment.
   * @param {string} id - Experiment id
   * @returns {Promise<Object>} { experiment, control_variant, variants, significance }
   */
  async getExperimentResults(id) {
    return this.get(`/experiments/${id}/results`);
  }

  /**
   * Create an experiment (starts in draft).
   * @param {Object} data - { key, name, hypothesis, variants, primary_metric,
   *   activation_window_hours, min_sample_per_variant }
   * @returns {Promise<Object>} created experiment (201)
   */
  async createExperiment(data) {
    return this.post("/experiments", data);
  }

  /**
   * Update an experiment (status transitions, edits). Key is immutable.
   * @param {string} id - Experiment id
   * @param {Object} data - Partial update
   * @returns {Promise<Object>} updated experiment
   */
  async updateExperiment(id, data) {
    return this.patch(`/experiments/${id}`, data);
  }
}

// Export singleton instance
export const adminApiService = new AdminApiService();
export default adminApiService;
